import { useState, useEffect, useRef } from "react";
import { Upload, FileText, Image, File, Trash2, Pencil, Check, X, Download, Copy, ChevronDown, ChevronRight, Eye, FolderOpen, Plus } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function FileIcon({ contentType }) {
  if (contentType?.startsWith("image/")) return <Image className="w-5 h-5 text-blue-500" />;
  if (contentType?.includes("pdf")) return <FileText className="w-5 h-5 text-red-500" />;
  return <File className="w-5 h-5 text-zinc-500" />;
}

function formatSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export default function ResourcesView({ members = [] }) {
  const memberNames = [...members].sort();
  const [resources, setResources] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("all");
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const [editCategory, setEditCategory] = useState("");
  const [editDesc, setEditDesc] = useState("");
  const [editFillable, setEditFillable] = useState(false);
  const [editFormFields, setEditFormFields] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [showUploadForm, setShowUploadForm] = useState(false);
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploadCategory, setUploadCategory] = useState("Member Resources");
  const [uploadDesc, setUploadDesc] = useState("");
  const [uploadFillable, setUploadFillable] = useState(false);
  const [uploadMember, setUploadMember] = useState("");
  const [customCategory, setCustomCategory] = useState("");
  const [fillForm, setFillForm] = useState(null);
  const [fillData, setFillData] = useState({});
  const [fillMember, setFillMember] = useState("");
  const [submittingForm, setSubmittingForm] = useState(false);
  const fileRef = useRef(null);

  const defaultCategories = ["New Member Packet", "Member Resources"];

  useEffect(() => { loadResources(); }, []);

  async function loadResources() {
    try {
      const res = await axios.get(`${API}/resources`);
      setResources(res.data || []);
    } catch { /* */ }
    finally { setLoaded(true); }
  }

  const categories = [...new Set([...defaultCategories, ...resources.map(r => r.category)])].sort();

  async function handleUpload(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("title", uploadTitle || file.name);
      const cat = uploadCategory === "__custom" ? customCategory : uploadCategory;
      form.append("category", cat || "Member Resources");
      form.append("description", uploadDesc);
      form.append("fillable", uploadFillable.toString());
      if (uploadMember) form.append("uploadedBy", uploadMember);
      await axios.post(`${API}/resources/upload`, form);
      await loadResources();
      setShowUploadForm(false);
      setUploadTitle("");
      setUploadCategory("Member Resources");
      setUploadDesc("");
      setUploadFillable(false);
      setUploadMember("");
      setCustomCategory("");
    } catch { /* */ }
    finally { setUploading(false); e.target.value = ""; }
  }

  async function deleteResource(id) {
    if (!window.confirm("Delete this resource?")) return;
    try {
      await axios.delete(`${API}/resources/${id}`);
      await loadResources();
    } catch { /* */ }
  }

  async function saveEdit(id) {
    try {
      await axios.put(`${API}/resources/${id}`, {
        title: editTitle,
        category: editCategory,
        description: editDesc,
        fillable: editFillable,
        formFields: editFillable ? editFormFields : [],
      });
      setEditingId(null);
      await loadResources();
    } catch { /* */ }
  }

  function startEdit(r) {
    setEditingId(r.resourceId);
    setEditTitle(r.title);
    setEditCategory(r.category);
    setEditDesc(r.description || "");
    setEditFillable(r.fillable || false);
    setEditFormFields(r.formFields || []);
  }

  function copyShareLink(resourceId) {
    const url = `${window.location.origin}/api/resources/${resourceId}/download`;
    navigator.clipboard.writeText(url).then(() => {
      setCopiedId(resourceId);
      setTimeout(() => setCopiedId(null), 2000);
    }).catch(() => {
      window.prompt("Copy this link:", url);
    });
  }

  async function viewResource(resourceId) {
    try {
      const res = await axios.get(`${API}/resources/${resourceId}`);
      const data = res.data;
      if (data.fileData) {
        if (data.contentType?.startsWith("image/")) {
          setPreviewUrl(data.fileData);
        } else {
          window.open(`${API}/resources/${resourceId}/download`, "_blank");
        }
      }
    } catch { /* */ }
  }

  async function openFillForm(resource) {
    try {
      const res = await axios.get(`${API}/resources/${resource.resourceId}`);
      const data = res.data;
      setFillForm(data);
      setFillData({});
      setFillMember("");
      setTimeout(() => {
        document.getElementById("fill-form-top")?.scrollIntoView({ behavior: "instant" });
      }, 50);
    } catch { /* */ }
  }

  async function submitFilledForm() {
    if (!fillMember || !fillForm) return;
    setSubmittingForm(true);
    try {
      await axios.post(`${API}/responses`, {
        memberName: fillMember,
        resourceId: fillForm.resourceId,
        resourceTitle: fillForm.title,
        formData: fillData,
      });
      setFillForm(null);
      setFillData({});
      setFillMember("");
    } catch { /* */ }
    finally { setSubmittingForm(false); }
  }


  const filtered = filter === "all" ? resources : resources.filter(r => r.category === filter);

  return (
    <div data-testid="resources-view">
      {/* Preview Modal */}
      {previewUrl && (
        <div className="fixed inset-0 z-50 bg-black/70" onClick={() => setPreviewUrl(null)}>
          <button onClick={() => setPreviewUrl(null)} className="absolute top-3 right-3 z-10 w-8 h-8 flex items-center justify-center bg-black/50 text-white rounded-full">
            <X className="w-5 h-5" />
          </button>
          <div className="w-full h-full sm:p-4 sm:flex sm:items-center sm:justify-center" onClick={e => e.stopPropagation()}>
            {previewUrl.includes("image/") ? (
              <img src={previewUrl} alt="" className="w-full h-full object-contain sm:max-w-4xl sm:max-h-[90vh] sm:rounded-lg" />
            ) : (
              <iframe src={previewUrl} className="w-full h-full sm:max-w-4xl sm:max-h-[90vh] sm:rounded-lg bg-white" title="Preview" />
            )}
          </div>
        </div>
      )}

      {/* Fill Form Modal */}
      {fillForm && (fillForm.formFields?.length > 0 ? (
        <div className="fixed inset-0 z-50 bg-black/50 sm:flex sm:items-center sm:justify-center" onClick={() => setFillForm(null)}>
          <div className="bg-white sm:rounded-2xl sm:max-w-lg w-full h-full sm:h-auto sm:max-h-[90vh] overflow-y-auto scroll-smooth" onClick={e => e.stopPropagation()}>
            <div id="fill-form-top" className="sticky top-0 bg-white border-b border-zinc-200 px-5 py-4 sm:rounded-t-2xl z-10">
              <div className="flex items-center justify-between">
                <h3 className="text-base font-bold text-zinc-900">{fillForm.title}</h3>
                <button onClick={() => setFillForm(null)}><X className="w-5 h-5 text-zinc-400" /></button>
              </div>
              <p className="text-[11px] text-zinc-500 mt-1">Fill out and submit. Everyone can see responses in the Responses tab.</p>
            </div>
            <div className="p-5 space-y-3">
              <div>
                <label className="block text-[10px] font-bold text-zinc-500 tracking-widest uppercase mb-1">Your Name *</label>
                <select value={fillMember} onChange={e => setFillMember(e.target.value)} className="w-full px-3 py-2.5 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500">
                  <option value="">Select your name</option>
                  {[...members].sort().map(n => <option key={n} value={n}>{n}</option>)}
                </select>
              </div>
              {(() => {
                const groups = [];
                let currentGroup = null;
                fillForm.formFields.forEach(field => {
                  const match = field.label.match(/^(.+?)\s*#\d+$/);
                  const groupName = match ? match[1] : null;
                  if (groupName && currentGroup?.name === groupName) {
                    currentGroup.fields.push(field);
                  } else if (groupName) {
                    currentGroup = { name: groupName, fields: [field] };
                    groups.push(currentGroup);
                  } else {
                    currentGroup = null;
                    groups.push({ name: null, fields: [field] });
                  }
                });
                return groups.map((group, gi) => {
                  if (group.name) {
                    return (
                      <div key={gi} className="bg-zinc-50 rounded-lg p-3">
                        <p className="text-[10px] font-bold text-zinc-500 tracking-widest uppercase mb-2">{group.name}</p>
                        <div className="grid grid-cols-2 gap-1.5">
                          {group.fields.map(field => {
                            const num = field.label.match(/#(\d+)$/)?.[1] || "";
                            return (
                              <div key={field.id} className="flex items-center gap-1.5">
                                <span className="text-[10px] font-bold text-zinc-400 w-4 text-right flex-shrink-0">{num}.</span>
                                <input
                                  value={fillData[field.label] || ""}
                                  onChange={e => setFillData(prev => ({ ...prev, [field.label]: e.target.value }))}
                                  className="flex-1 px-2 py-1.5 text-[12px] border border-zinc-200 rounded outline-none focus:border-orange-500"
                                  placeholder={`#${num}`}
                                />
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  }
                  return group.fields.map(field => (
                    <div key={field.id}>
                      <label className="block text-[10px] font-bold text-zinc-500 tracking-widest uppercase mb-1">{field.label}</label>
                      {field.type === "textarea" ? (
                        <textarea
                          value={fillData[field.label] || ""}
                          onChange={e => setFillData(prev => ({ ...prev, [field.label]: e.target.value }))}
                          rows={3}
                          className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500 resize-none"
                        />
                      ) : (
                        <input
                          value={fillData[field.label] || ""}
                          onChange={e => setFillData(prev => ({ ...prev, [field.label]: e.target.value }))}
                          className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500"
                        />
                      )}
                    </div>
                  ));
                });
              })()}
              <button
                onClick={submitFilledForm}
                disabled={submittingForm || !fillMember}
                className="w-full py-3 text-sm font-bold text-white bg-zinc-900 hover:bg-zinc-800 rounded-xl disabled:opacity-40 transition-colors"
              >
                {submittingForm ? "Submitting..." : "Submit Response"}
              </button>
            </div>
          </div>
        </div>
      ) : (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setFillForm(null)}>
          <div className="bg-white rounded-2xl p-6 max-w-sm w-full text-center" onClick={e => e.stopPropagation()}>
            <p className="text-sm text-zinc-600 mb-3">Loading form fields...</p>
            <button onClick={() => setFillForm(null)} className="px-5 py-2 text-sm font-bold text-white bg-zinc-900 rounded-xl">Close</button>
          </div>
        </div>
      ))}


      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-base font-bold text-zinc-900">Member Resources</h3>
        <div className="flex gap-2">
          <button
            onClick={() => {
              const url = `${window.location.origin}/packet`;
              navigator.clipboard.writeText(url).then(() => {
                setCopiedId("packet");
                setTimeout(() => setCopiedId(null), 2000);
              }).catch(() => window.prompt("Copy this link:", url));
            }}
            className={`inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg transition-colors ${
              copiedId === "packet" ? "bg-green-100 text-green-700" : "bg-blue-100 text-blue-700 hover:bg-blue-200"
            }`}
          >
            {copiedId === "packet" ? <><Check className="w-3.5 h-3.5" /> Link Copied</> : <><Copy className="w-3.5 h-3.5" /> Share Packet</>}
          </button>
          <button
            onClick={() => setShowUploadForm(!showUploadForm)}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white bg-zinc-900 hover:bg-zinc-800 rounded-lg transition-colors"
          >
            <Upload className="w-3.5 h-3.5" /> Upload
          </button>
        </div>
      </div>

      {/* Upload Form */}
      {showUploadForm && (
        <div className="bg-zinc-50 border border-zinc-200 rounded-xl p-4 mb-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <label className="block text-[10px] font-bold text-zinc-500 tracking-widest uppercase mb-1">Your Name</label>
              <select value={uploadMember} onChange={e => setUploadMember(e.target.value)} data-testid="upload-member-select" className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500">
                <option value="">— select —</option>
                {memberNames.map(m => <option key={m} value={m}>{m}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-[10px] font-bold text-zinc-500 tracking-widest uppercase mb-1">Title</label>
              <input value={uploadTitle} onChange={e => setUploadTitle(e.target.value)} className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500" placeholder="Resource title (or uses filename)" />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-zinc-500 tracking-widest uppercase mb-1">Category</label>
              <select value={uploadCategory} onChange={e => setUploadCategory(e.target.value)} className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500">
                {categories.map(c => <option key={c} value={c}>{c}</option>)}
                <option value="__custom">+ New Category</option>
              </select>
            </div>
          </div>
          {uploadCategory === "__custom" && (
            <input value={customCategory} onChange={e => setCustomCategory(e.target.value)} className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500" placeholder="New category name" />
          )}
          <div>
            <label className="block text-[10px] font-bold text-zinc-500 tracking-widest uppercase mb-1">Description (optional)</label>
            <input value={uploadDesc} onChange={e => setUploadDesc(e.target.value)} className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500" placeholder="Brief description..." />
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={uploadFillable} onChange={e => setUploadFillable(e.target.checked)} className="w-4 h-4 rounded border-zinc-300 text-orange-600 focus:ring-orange-500" />
            <span className="text-xs font-bold text-zinc-600">Make this fillable (members can complete it in the system)</span>
          </label>
          <div className="flex gap-2">
            <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.gif,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt" onChange={handleUpload} className="hidden" />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={uploading}
              className="inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors disabled:opacity-50"
            >
              {uploading ? "Uploading..." : <><Plus className="w-3.5 h-3.5" /> Choose File & Upload</>}
            </button>
            <button onClick={() => setShowUploadForm(false)} className="px-3 py-2.5 text-xs font-bold text-zinc-600 bg-zinc-200 hover:bg-zinc-300 rounded-lg transition-colors">
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Category Filter */}
      <div className="flex gap-1 flex-wrap mb-4">
        <button
          onClick={() => setFilter("all")}
          className={`px-3 py-1 text-[11px] font-bold rounded-md transition-colors ${filter === "all" ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
        >
          All ({resources.length})
        </button>
        {categories.map(c => {
          const count = resources.filter(r => r.category === c).length;
          if (count === 0 && !defaultCategories.includes(c)) return null;
          return (
            <button
              key={c}
              onClick={() => setFilter(c)}
              className={`px-3 py-1 text-[11px] font-bold rounded-md transition-colors ${filter === c ? "bg-zinc-900 text-white" : "bg-zinc-100 text-zinc-600 hover:bg-zinc-200"}`}
            >
              {c} ({count})
            </button>
          );
        })}
      </div>

      {/* Resource List */}
      {!loaded ? (
        <div className="flex justify-center py-8">
          <div className="w-6 h-6 border-3 border-zinc-300 border-t-zinc-600 rounded-full animate-spin" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12">
          <FolderOpen className="w-10 h-10 text-zinc-300 mx-auto mb-3" />
          <p className="text-sm text-zinc-400">{filter === "all" ? "No resources uploaded yet." : `No resources in "${filter}".`}</p>
          <p className="text-xs text-zinc-300 mt-1">Upload PDFs, images, or documents for your members.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {filtered.map(r => (
            <div key={r.resourceId} className="border border-zinc-200 rounded-lg bg-white hover:border-zinc-300 transition-colors">
              {editingId === r.resourceId ? (
                <div className="p-3 space-y-2">
                  <input value={editTitle} onChange={e => setEditTitle(e.target.value)} className="w-full px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none focus:border-orange-500 font-semibold" />
                  <div className="grid grid-cols-2 gap-2">
                    <select value={editCategory} onChange={e => setEditCategory(e.target.value)} className="px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none">
                      {categories.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                    <input value={editDesc} onChange={e => setEditDesc(e.target.value)} className="px-3 py-2 text-sm border border-zinc-200 rounded-lg outline-none" placeholder="Description..." />
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={editFillable} onChange={e => setEditFillable(e.target.checked)} className="w-4 h-4 rounded border-zinc-300 text-orange-600 focus:ring-orange-500" />
                    <span className="text-xs font-bold text-zinc-600">Fillable form</span>
                  </label>
                  {editFillable && (
                    <div className="bg-zinc-50 rounded-lg p-3 space-y-2">
                      <p className="text-[10px] font-bold text-zinc-400 uppercase tracking-widest">Form Fields</p>
                      {editFormFields.map((f, i) => (
                        <div key={i} className="flex gap-2 items-center">
                          <input
                            value={f.label}
                            onChange={e => {
                              const updated = [...editFormFields];
                              updated[i] = { ...f, label: e.target.value, id: e.target.value.toLowerCase().replace(/[^a-z0-9]/g, "_").slice(0, 20) };
                              setEditFormFields(updated);
                            }}
                            className="flex-1 px-2 py-1.5 text-[11px] border border-zinc-200 rounded outline-none focus:border-orange-500"
                            placeholder="Field label"
                          />
                          <select
                            value={f.type}
                            onChange={e => {
                              const updated = [...editFormFields];
                              updated[i] = { ...f, type: e.target.value };
                              setEditFormFields(updated);
                            }}
                            className="px-2 py-1.5 text-[11px] border border-zinc-200 rounded outline-none w-24"
                          >
                            <option value="text">Short text</option>
                            <option value="textarea">Long text</option>
                          </select>
                          <button
                            onClick={() => setEditFormFields(editFormFields.filter((_, j) => j !== i))}
                            className="p-1 text-red-400 hover:text-red-600"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                      <button
                        onClick={() => setEditFormFields([...editFormFields, { id: `field_${editFormFields.length}`, label: "", type: "text" }])}
                        className="w-full py-1.5 text-[11px] font-bold text-orange-600 bg-orange-50 hover:bg-orange-100 border border-dashed border-orange-200 rounded-md transition-colors"
                      >
                        + Add Field
                      </button>
                    </div>
                  )}
                  <div className="flex gap-1.5">
                    <button onClick={() => saveEdit(r.resourceId)} className="inline-flex items-center gap-1 px-3 py-1.5 text-[11px] font-bold text-white bg-green-600 rounded-md"><Check className="w-3 h-3" /> Save</button>
                    <button onClick={() => setEditingId(null)} className="px-3 py-1.5 text-[11px] font-bold text-zinc-600 bg-zinc-100 rounded-md">Cancel</button>
                  </div>
                </div>
              ) : (
                <div className="px-4 py-3">
                  <div className="flex items-start gap-3">
                    <FileIcon contentType={r.contentType} />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-zinc-900 truncate">{r.title}</p>
                      <div className="flex items-center gap-2 text-[10px] text-zinc-400 flex-wrap">
                        <span className="px-1.5 py-0.5 bg-zinc-100 rounded text-[10px] font-bold text-zinc-500">{r.category}</span>
                        {r.fillable && <span className="px-1.5 py-0.5 bg-orange-100 rounded text-[10px] font-bold text-orange-600">Fillable</span>}
                        <span>{formatSize(r.fileSize)}</span>
                        {r.uploadedBy && <span>· by <span className="font-semibold text-zinc-600">{r.uploadedBy}</span></span>}
                      </div>
                      {r.description && <p className="text-[11px] text-zinc-500 mt-0.5">{r.description}</p>}
                    </div>
                  </div>
                  {r.fillable && (
                    <button onClick={() => openFillForm(r)} className="w-full flex items-center justify-center gap-1.5 py-2 mt-2 text-xs font-bold text-white bg-orange-600 hover:bg-orange-700 rounded-lg transition-colors">
                      <FileText className="w-3.5 h-3.5" /> Fill Out This Form
                    </button>
                  )}
                  <div className="flex items-center gap-1 mt-2 pt-2 border-t border-zinc-100">
                    <button onClick={() => viewResource(r.resourceId)} className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 text-[11px] font-bold text-zinc-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors">
                      <Eye className="w-3.5 h-3.5" /> View
                    </button>
                    <a href={`${API}/resources/${r.resourceId}/download`} className="flex-1 inline-flex items-center justify-center gap-1 py-1.5 text-[11px] font-bold text-zinc-500 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors">
                      <Download className="w-3.5 h-3.5" /> Save
                    </a>
                    <button onClick={() => copyShareLink(r.resourceId)} className={`flex-1 inline-flex items-center justify-center gap-1 py-1.5 text-[11px] font-bold rounded-md transition-colors ${copiedId === r.resourceId ? "text-green-600 bg-green-50" : "text-zinc-500 hover:text-purple-600 hover:bg-purple-50"}`}>
                      {copiedId === r.resourceId ? <><Check className="w-3.5 h-3.5" /> Copied</> : <><Copy className="w-3.5 h-3.5" /> Share</>}
                    </button>
                    <button onClick={() => startEdit(r)} className="p-1.5 text-zinc-400 hover:text-orange-600 hover:bg-orange-50 rounded-md transition-colors">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => deleteResource(r.resourceId)} className="p-1.5 text-zinc-300 hover:text-red-500 hover:bg-red-50 rounded-md transition-colors">
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
